import React from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';

import { getBrand } from 'flow/brands/brands';
import { setPreventUnload } from 'flow/actions/app-actions';
import { showImagePreview } from 'flow/actions/chat-actions';
import Page from 'flow/components/Setting/Page';

const INTRO_GIF = require('images/tickets/tickets_intro.gif');

const HEADLINE_MAIN = 'route.tickets';
const TEXT_BODY_INTRO_MAIN = 'tickets.intro.body.main';
const TEXT_BODY_INTRO_MAIN_NON_ADMIN = 'tickets.intro.body.main.nonadmin';
const TEXT_BODY_WELCOME_DESCRIPTION =
    'tickets.intro.body.welcome_feature_description';

const HEADLINE_GET_STARTED = 'tickets.intro.get_started';

const BODY_BULLET_ONE = 'tickets.intro.open_settings';

const BODY_BULLET_TWO = 'tickets.intro.choose_tickets';

const TEXT_READ_MORE = 'tickets.intro.read_more';
const TEXT_SMS_COST = 'tickets.intro.sms_cost';

const TALK_TO_ADMIN_ONE = 'tickets.intro.body.enabled_admin';
const TALK_TO_ADMIN_TWO = 'tickets.intro.body.reach_out_admin';

const TELAVOX_TICKETS_INFO_URL = 'https://support.telavox.com/en_US/tickets';

interface NumberTextItemProps {
    textKey: string;
    number: number;
}

const NumberTextItem = React.memo(
    ({ textKey, number }: NumberTextItemProps) => {
        const { i18next } = useAppContext();

        return (
            <div className="flex flex-row pt-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-gray-100">
                    <div className="text-base font-medium text-gray-800">
                        {number}
                    </div>
                </div>
                <div className="flex flex-col justify-center ps-3">
                    <div className="text-base text-gray-800">
                        {i18next.t(textKey)}
                    </div>
                </div>
            </div>
        );
    },
);

NumberTextItem.displayName = 'NumberTextItem';

const getShouldDisplayMoreInfoTelavoxUrl = () => {
    return getBrand().brandNameUIFriendly === 'Telavox';
};

interface IntroViewProps {
    messageIsAdaptedToNonAdminUser: boolean;
}

const TicketsIntroView = ({
    messageIsAdaptedToNonAdminUser,
}: IntroViewProps) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const preventCleanupOnBeforeunload = () => {
        setPreventUnload(true);
        // About this:
        // Some years ago we discovered that window event beforeunload was triggered in some cases when clicking a url,
        // which would cause us to do a cleanup, such as clearing the redux store and closing the Top connection.
        // This is something we want done when the user closes the page (= beforeunload is triggered), but not when
        // a url is clicked. Method setPreventUnload (which I today would have named setPreventCleanupOnBeforeUnload)
        // is used onClick on a href and prevents the cleanup from happening if beforeunload is triggered (within the next 500ms)
    };

    return (
        <Page header={HEADLINE_MAIN}>
            <div className="flex flex-col px-3">
                <div className="flex flex-col">
                    <img
                        className="flex max-w-[600] cursor-pointer justify-center pb-8"
                        src={INTRO_GIF}
                        id={'tickets_intro_gif'}
                        alt={'Tickets preview'}
                        onClick={() => dispatch(showImagePreview(INTRO_GIF))}
                    />
                    <div className="pb-8 text-xl text-gray-800">
                        {messageIsAdaptedToNonAdminUser
                            ? i18next.t(TEXT_BODY_INTRO_MAIN_NON_ADMIN)
                            : i18next.t(TEXT_BODY_INTRO_MAIN)}
                    </div>
                    <div className="pb-8 text-base font-light tracking-normal text-gray-800">
                        {i18next.t(TEXT_BODY_WELCOME_DESCRIPTION)}
                    </div>
                </div>
                <div className="text-sm font-bold text-gray-800">
                    {`${i18next.t(HEADLINE_GET_STARTED)}`}
                </div>
                {messageIsAdaptedToNonAdminUser ? (
                    <div className="pt-3 text-base font-light tracking-normal text-gray-800">
                        <div>{i18next.t(TALK_TO_ADMIN_ONE)}</div>
                        <div>{i18next.t(TALK_TO_ADMIN_TWO)}</div>
                    </div>
                ) : null}
                <div className="flex flex-col pb-8">
                    <NumberTextItem textKey={BODY_BULLET_ONE} number={1} />
                    <NumberTextItem textKey={BODY_BULLET_TWO} number={2} />
                </div>
                {getShouldDisplayMoreInfoTelavoxUrl() ? (
                    <div className="flex flex-col pb-8 text-base font-light text-gray-800">
                        <a
                            href={TELAVOX_TICKETS_INFO_URL}
                            target={'_blank'}
                            onClick={preventCleanupOnBeforeunload}
                            className="text-link flex w-fit pt-2"
                            rel={'noreferrer'}
                        >
                            {i18next.t(TEXT_READ_MORE)}
                        </a>
                    </div>
                ) : null}
                <div className="pb-4 text-xs italic">
                    {i18next.t(TEXT_SMS_COST)}
                </div>
            </div>
        </Page>
    );
};

export default React.memo(TicketsIntroView);
